import type { AccountType } from '@/api/models';
import { cn } from '@/utils/cnUtils';
import type { AccountTreeNode } from '../utils';

/**
 * Presentational config for the Chart of Accounts grid — kept out of the `.tsx`
 * files per the repo's render/logic split.
 */

/** Stable column ids, shared between the header and the tree column prop. */
export const COA_COLUMN = {
    account: 'account',
    code: 'code',
    type: 'type',
    balance: 'balance',
} as const;

/** The tree (expandable) column — the ACCOUNT column carries the chevron. */
export const COA_TREE_COLUMN = COA_COLUMN.account;

/**
 * Pixels of indent added per nesting level in the ACCOUNT column. A generous
 * step makes deep nesting (e.g. "Current liabilities" under "Liabilities")
 * read as clearly subordinate on the full chart — each level is offset far
 * enough that a sub-group header can't be mistaken for a top-level group.
 */
export const COA_INDENT_STEP_PX = 28;

/**
 * Row and header heights, in pixels, for DataGrid's virtualization.
 *
 * `virtualized` renders only the rows in view, and it needs a *fixed* row
 * height to compute the scroll window — these must match what the CSS actually
 * produces or rows drift out of alignment with the scrollbar. Measured against
 * the rendered grid; change them together with any padding change to
 * `.coa-data-grid` rows.
 *
 * Why virtualize a ~260-row chart when the docs suggest 1,000+: every toggle of
 * `expandedKeys` re-renders the whole grid, and each row carries a Tooltip and
 * Button (React Aria overlay machinery), so per-row cost is ~5.5 ms. At 235
 * rendered rows a single expand blocked the main thread for ~1.3 s regardless
 * of how many rows the expand actually added. Virtualizing cuts the rendered
 * set to what is on screen, which is what makes the cost proportional to the
 * viewport instead of to the chart.
 */
export const COA_ROW_HEIGHT_PX = 57;
export const COA_HEADING_HEIGHT_PX = 36;

/**
 * The grid's scroll viewport. Virtualization requires a bounded height — the
 * grid scrolls internally rather than growing the page.
 */
export const COA_CONTENT_CLASS =
    'h-[calc(100vh-19rem)] min-h-[24rem] min-w-[640px] overflow-auto';

/**
 * Per-type chip styling (the account TYPE tag, rendered on leaf rows only).
 *
 * HeroUI's `Chip` `color` prop only covers accent/success/warning/danger, but
 * the design calls for distinct hues per type. So each type maps to a soft
 * pill className built from Tailwind's palette. Following this theme's
 * soft-chip philosophy, the pills render the SAME in light and dark (an opaque
 * pale tint + saturated foreground) — no `dark:` variants, which would track
 * the OS setting instead of the app's theme toggle.
 *
 * Colours (established with design): Liability = amber, Equity = violet,
 * Revenue = teal, Expense = neutral/gray. Asset = sky/blue (assumed — confirm
 * the Asset token with design; see the task report's Open items).
 */
export const ACCOUNT_TYPE_CHIP_CLASS: Record<AccountType, string> = {
    ASSET: 'bg-sky-100 text-sky-800',
    LIABILITY: 'bg-amber-100 text-amber-800',
    EQUITY: 'bg-violet-100 text-violet-800',
    REVENUE: 'bg-teal-100 text-teal-800',
    EXPENSE: 'bg-neutral-200 text-neutral-700',
};

/** i18n key (namespace `pages`) for each type's chip label. */
export const ACCOUNT_TYPE_LABEL_KEY: Record<AccountType, string> = {
    ASSET: 'chartOfAccounts.types.asset',
    LIABILITY: 'chartOfAccounts.types.liability',
    EQUITY: 'chartOfAccounts.types.equity',
    REVENUE: 'chartOfAccounts.types.revenue',
    EXPENSE: 'chartOfAccounts.types.expense',
};

/**
 * Whether a row navigates when activated: a postable leaf. Header/aggregate
 * rows (or any row with children) never navigate — the chevron is their only
 * control — so navigation and the expand affordance can't overlap on one row.
 * Drives both the DataGrid `onRowAction` gate and the "View entries" affordance.
 */
export const isNavigableAccountRow = (node: AccountTreeNode): boolean =>
    node.account.isPostable && !node.hasChildren;

/**
 * ACCOUNT-column name weight. Top headers are boldest; each deeper sub-header
 * lightens so subordinate levels read as such — the weight ladder works with
 * the per-depth indent (`COA_INDENT_STEP_PX`) so nesting is unambiguous. Beyond
 * the second level, headers also drop to the muted foreground so a deep
 * sub-group can't be confused with a top-level one. Leaves are regular weight.
 */
export const getAccountNameClassName = (node: AccountTreeNode): string => {
    if (!node.hasChildren) {
        return 'font-normal text-foreground';
    }
    if (node.depth === 0) {
        return 'font-bold text-foreground';
    }
    if (node.depth === 1) {
        return 'font-semibold text-foreground';
    }
    return 'font-medium text-muted';
};

/** Right-aligned tabular balance; negative / contra nets render in danger. */
export const getBalanceClassName = (displayTetri: number): string =>
    cn(
        'text-right text-sm tabular-nums',
        displayTetri < 0 ? 'text-danger' : 'text-foreground',
        'font-medium',
    );
